import React, { useState } from 'react';

import { useObserver } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Notification, Button, ConfirmModal } from '@bringg/react-components';
import { Webhook } from '@bringg-frontend/webhooks-api-internal-stores';

interface Props {
	webhook: Webhook;
	closeEdit: () => void;
	isNew?: boolean;
}

const WebhooksEditActions: React.FC<Props> = ({ closeEdit, webhook, isNew }) => {
	const { t } = useTranslation();
	const [loadingSave, setLoadingSave] = useState(false);
	const [loadingDelete, setLoadingDelete] = useState(false);

	const asyncSaveWebhook = async () => {
		setLoadingSave(true);
		await webhook.save();
		closeEdit();
	};

	const asyncDeleteWebhook = async () => {
		setLoadingDelete(true);
		await webhook.delete();
		closeEdit();
	};

	const saveWebhook = () => {
		(!isNew
			? new Promise((resolve, reject) => {
					try {
						const modalBody =
							'This is a live webhook, changing it might cause a different behavior! Are you sure you want to save your changes?';

						ConfirmModal({
							title: t('WEBHOOK_APP.MODAL.CONFIRM_SAVE_MODAL_TITLE', { defaultValue: 'Save Webhook' }),
							content: t('WEBHOOK_APP.MODAL.CONFIRM_SAVE_MODAL_CONTENT', { defaultValue: modalBody }),
							okText: t('WEBHOOK_APP.MODAL.CONFIRM_SAVE_MODAL_OK', { defaultValue: 'OK' }),
							cancelText: t('WEBHOOK_APP.MODAL.CONFIRM_SAVE_MODAL_CANCEL', { defaultValue: 'Cancel' }),
							okType: 'primary',
							onOk() {
								resolve(true);
							}
						});
					} catch (e) {
						reject(e);
					}
			  })
			: Promise.resolve()
		)
			.then(async () => {
				return asyncSaveWebhook();
			})
			.catch(e => {
				console.error('Webhook saving failed', e);
				Notification.error(t('WEBHOOK_APP.MODAL.SAVING_ERROR', { defaultValue: 'Webhook saving failed' }));
			});
	};

	const deleteWebhook = () => {
		const modalBody =
			'This is a live webhook, Changing it can cause a change in the current behavior! Are you sure you want to delete it?';

		ConfirmModal({
			title: t('WEBHOOK_APP.MODAL.DELETE_WEBHOOK_TITLE', { defaultValue: 'Delete Webhook' }),
			content: t('WEBHOOK_APP.MODAL.DELETE_WEBHOOK_TEXT', {
				defaultValue: modalBody
			}),
			okText: t('WEBHOOK_APP.MODAL.DELETE_WEBHOOK_OK_BUTTON', { defaultValue: 'OK' }),
			okType: 'danger',
			cancelText: t('WEBHOOK_APP.MODAL.DELETE_WEBHOOK_CANCEL_BUTTON', { defaultValue: 'Cancel' }),
			onOk() {
				asyncDeleteWebhook().catch(e => {
					console.error('Webhook deletion failed', e);
					Notification.error(
						t('WEBHOOK_APP.MODAL.DELETION_ERROR', { defaultValue: 'Webhook deletion failed' })
					);
				});
			}
		});
	};

	return useObserver(() => {
		return (
			<div className="webhooks-edit-actions">
				{!isNew ? (
					<Button
						onClick={deleteWebhook}
						className="delete-button"
						key="delete"
						loading={loadingDelete}
						danger
					>
						{t('WEBHOOK_APP.MODAL.DELETE_BUTTON', { defaultValue: 'Delete' })}
					</Button>
				) : (
					''
				)}
				<Button onClick={closeEdit} className="cancel-button" key="cancel">
					{t('WEBHOOK_APP.MODAL.CANCEL_BUTTON', { defaultValue: 'Cancel' })}
				</Button>
				<Button
					onClick={saveWebhook}
					className="save-button"
					key="save"
					type="primary"
					disabled={!webhook.isValid}
					loading={loadingSave}
				>
					{t('WEBHOOK_APP.MODAL.SAVE_BUTTON', { defaultValue: 'Save' })}
				</Button>
			</div>
		);
	});
};

export default WebhooksEditActions;
